import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/Button/DefaultButton/Button';
import { Modal, ModalTitle, ModalBody, ModalContent, ModalFooter, BackDrop, ModalInput, Spinner, Divider } from './ModalSubmit.styles';
import DatePicker from '@/components/input/DatePicker/DatePicker';
import { handleNext } from '@helper/navigationHelpers';
import { useNavigate } from 'react-router-dom';
import { pages } from '../../../defaultVerbiages';
import { Slider } from '@/components/Slider/Slider';
import { makePostCall } from '@/services/makePostCall';
import SafeHTML from '@/utils/safeHTML';
import { useAppSelector, useAppDispatch } from '@app/hooks';
import { startLoading, stopLoading, selectIsLoading, setIsFirstTimeGetStarted, selectIsFirstTimeGetStarted } from '@/reducers/currentPage';
import { DefaultModal } from '../DefaultModal/DefaultModal';
import { performStatedCreditPatch } from '@/utils/handleEndpoints';
import { makePatchCall } from '@/services/makePatchCall';

import { TextInput } from '@/components/input/TextInput/TextInput';
import _ from 'lodash';
import { RootState } from '@/app/store';

export interface ModalSubmitProps {
  openModal: boolean;
  setOpenModal: (isOpen: boolean) => void;
  onAccess: (page: string) => void;
  infoSubmitModal: {
    label?: string;
    header?: string;
    body?: string;
    button: string;
    textOne?: string;
    textTwo?: string;
  };
  hasDataChanged?: () => boolean;
  resetChangeDetection?: () => void;
}

type Borrower = {
  dateofBirth: string;
  email: string;
  firstName: string;
  lastName: string;
  middleName: string;
  suffixName: string;
  isMailingAddressSameAsProperty: boolean;
  propertyOccupancyDate: string;
};
type Property = {
  city: string;
  county: string;
  loanPurposeType: string;
  postalCode: string;
  propertyType: string;
  propertyUseType: string;
  state: string;
  streetAddress: string;
  unitType: string;
  unit?: string;
};
type IcePayload = {
  borrower?: Borrower;
  loanFolder?: string;
  property?: Property;
  dateofBirth?: string;
  firstName?: string;
  lastName?: string;
  address?: {
    streetAddress: string;
    city: string;
    state: string;
    postalCode: string;
    unitNumber?: string;
  };
  preventSoftPullCredit?: boolean;
};

export const ModalSubmit: React.FC<ModalSubmitProps> = ({ openModal, setOpenModal, infoSubmitModal, onAccess, hasDataChanged, resetChangeDetection }) => {
  let currentForm = useAppSelector((state) => state.CurrentForm);
  const navigate = useNavigate();
  let loanApplication = useAppSelector((state) => state.LoanApplication);
  let templateConfig = useAppSelector((state) => state.TemplateConfig);
  const dispatch = useAppDispatch();
  const isLoading = useAppSelector(selectIsLoading);
  const isFirstTimeGetStarted = useAppSelector(selectIsFirstTimeGetStarted);
  let auxLoanApplication = _.cloneDeep(loanApplication);
  let isScoreNeeded = !currentForm?.softPullConsent;
  const [scoreNeeded, setScoreNeeded] = useState(isScoreNeeded);
  const errorRef = useRef<string | unknown>(null);
  const openErrorModalRef = useRef(false);
  const [, forceUpdate] = useState({});
  const canContinue = loanApplication?.borrowers[0]?.borrowerDOB !== '';
  const [isValidDateOfBirth, setIsValidDateOfBirth] = useState(false);
  const didValidateRef = useRef(false);
  const sliderConfig = useAppSelector((state) => state.TemplateConfig?.pages?.slider?.score) || [];
  const globalMin = sliderConfig[0]?.minRank || 300;
  const globalMax = sliderConfig[sliderConfig.length - 1]?.maxRank || 850;

  const storedCreditScore = useAppSelector((state: RootState) => state.LoanApplication.borrowers?.[0]?.creditScore);
  const [creditScore, setCreditScore] = useState<number>(storedCreditScore || globalMin);

  // store the initial credit score
  const initialCreditScoreRef = useRef<number>(storedCreditScore || globalMin);

  // set initial credit score only once
  useEffect(() => {
    if (initialCreditScoreRef.current === globalMin && storedCreditScore) {
      initialCreditScoreRef.current = storedCreditScore;
    }
  }, [storedCreditScore, globalMin]);
  const [isCreditScoreError, setIsCreditScoreError] = useState(false);

  const convertDateFormat = (dateString: string): string => {
    if (!dateString) return '';
    const [month, day, year] = dateString.split('/');
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  };

  const handleSliderChange = (value: number) => {
    const numericValue = parseInt(value.toString(), 10);

    const isValid = !isNaN(numericValue) && numericValue >= globalMin && numericValue <= globalMax;
    setIsCreditScoreError(!isValid);
    setCreditScore(value);

    if (isValid) {
      dispatch({
        type: 'LoanApplication/updateSpecificField',
        payload: {
          depth: ['borrowers', 0, 'creditScore'],
          value: numericValue,
        },
      });
    }
  };

  const handleInputChange = (value: string | number) => {
    const numericValue = parseInt(value.toString(), 10);

    const isValid = !isNaN(numericValue) && numericValue >= globalMin && numericValue <= globalMax;

    setCreditScore(numericValue);
    setIsCreditScoreError(!isValid);

    if (isValid) {
      dispatch({
        type: 'LoanApplication/updateSpecificField',
        payload: {
          depth: ['borrowers', 0, 'creditScore'],
          value: numericValue,
        },
      });
    }
  };

  const closeModal = (action?: any) => {
    setOpenModal(false);

    if (action === 'next') {
      // Set isFirstTimeGetStarted to false when actually navigating away from GetStarted page
      if (isFirstTimeGetStarted) {
        dispatch(setIsFirstTimeGetStarted(false));
      }
      handleNext(navigate, onAccess, 'get-started', '/get-started-address');
    }
  };

  const createIcePayload = (loanApplication: any, type: string): IcePayload => {
    const { propertyType, propertyPrimaryUse, borrowers, borrowerProperty } = loanApplication;
    const { propertyOccupancyDate, propertyCity, propertyCounty, propertyZip, propertyState, propertyStreetAddress, propertyStreetAddress2 } = borrowerProperty || ({} as any);

    if (type === 'loans') {
      return {
        borrower: {
          email: borrowers[0].borrowerEmailAddress,
          firstName: borrowers[0].borrowerFirstName,
          ...(borrowers[0].borrowerDOB && { dateofBirth: convertDateFormat(borrowers[0].borrowerDOB) }),
          lastName: borrowers[0].borrowerLastName,
          middleName: '',
          suffixName: '',
          isMailingAddressSameAsProperty: true,
          propertyOccupancyDate,
        },
        loanFolder: 'Training',
        property: {
          city: propertyCity,
          county: propertyCounty || ' ',
          loanPurposeType: borrowers[0].borrowerLoanPurpose,
          postalCode: propertyZip,
          propertyType: propertyType,
          propertyUseType: propertyPrimaryUse,
          state: propertyState,
          streetAddress: propertyStreetAddress,
          unit: propertyStreetAddress2 || '',
          unitType: '',
        },
      };
    } else {
      return {
        ...(borrowers[0].borrowerDOB && { dateofBirth: convertDateFormat(borrowers[0].borrowerDOB) }),
        firstName: borrowers[0].borrowerFirstName,
        lastName: borrowers[0].borrowerLastName,
        address: {
          streetAddress: propertyStreetAddress || borrowerProperty?.propertyStreetAddress || '',
          city: propertyCity || borrowerProperty?.propertyCity || '',
          state: propertyState || borrowerProperty?.propertyState || '',
          postalCode: propertyZip || borrowerProperty?.propertyZip || '',
        },
      };
    }
  };

  const processClick = async () => {
    const isICE = templateConfig?.isICE ?? false;
    const isResume = loanApplication.isEligible ?? false;
    const hasLoanInfo = loanApplication.loanId !== '' && loanApplication.loanNumber !== '';
    const isSoftCreditPullSelected = currentForm?.softPullConsent ?? false;

    auxLoanApplication.loanId = loanApplication.loanId || auxLoanApplication.loanId || '';
    auxLoanApplication.loanNumber = loanApplication.loanNumber || auxLoanApplication.loanNumber || '';

    // current/original values no longer used for preventSoftPullCredit logic

    // let preventSoftPullCredit = false;
    // if (isSoftCreditPullSelected) {
    //   if (isFirstTimeGetStarted || sensitiveFieldsChanged) {
    //     preventSoftPullCredit = false;
    //   } else {
    //     preventSoftPullCredit = true;
    //   }
    // }

    // Always run soft pull if consent selected; do not skip even if nothing changed

    // check if we should skip API call when no soft credit consent
    if (!isSoftCreditPullSelected && !isFirstTimeGetStarted) {
      const anyFieldsChanged = hasDataChanged ? hasDataChanged() : false;

      // check credit score changes (in LoanApplication)
      const currentCreditScore = loanApplication?.borrowers[0]?.creditScore || 0;
      const initialStoredCreditScore = initialCreditScoreRef.current;
      const creditScoreChanged = currentCreditScore !== initialStoredCreditScore;

      const anyChangesDetected = anyFieldsChanged || creditScoreChanged;

      if (!anyChangesDetected) {
        closeModal('next');
        return;
      } else {
      }
    }

    dispatch(startLoading());
    try {
      didValidateRef.current = false;
      if (!scoreNeeded || (isICE && !hasLoanInfo)) {
        const endpointSoftpull = hasLoanInfo ? `/file/${loanApplication.loanId}/softpull-credit` : '/file/softpull-credit';

        const method = hasLoanInfo ? 'PATCH' : 'POST';
        const endpoint = isICE && !hasLoanInfo ? `/loans` : endpointSoftpull;

        if (isICE && hasLoanInfo && isSoftCreditPullSelected) {
          dispatch(startLoading());
          const spEndpoint = `/loans/${loanApplication.loanId}/soft-credit-pull`;
          const spMethod = 'POST';
          const spPayload = { ...createIcePayload(loanApplication, 'softpull') };

          const spAction = {
            actionProperties: {
              endpoint: spEndpoint,
              method: spMethod,
            },
          };

          const spResponse = await makePostCall(spAction, auxLoanApplication, spPayload, dispatch, templateConfig);
          const spData = (spResponse && (spResponse as any).payload) ?? (spResponse && (spResponse as any).data) ?? spResponse;

          if (auxLoanApplication.loanId) {
            dispatch({ type: 'LoanApplication/updateSpecificField', payload: { depth: ['loanId'], value: auxLoanApplication.loanId } });
          }
          if (auxLoanApplication.loanNumber) {
            dispatch({ type: 'LoanApplication/updateSpecificField', payload: { depth: ['loanNumber'], value: auxLoanApplication.loanNumber } });
          }

          const finalResponseData = spData;
          let statedCreditRequired = false;
          if (Array.isArray(finalResponseData)) {
            const borrowersItem = finalResponseData.find((item: { depth?: string[]; payload?: { statedCreditRequired?: boolean } }) => item.depth?.includes('borrowers'));
            if (borrowersItem && typeof borrowersItem.payload?.statedCreditRequired === 'boolean') {
              statedCreditRequired = borrowersItem.payload.statedCreditRequired;
            } else {
              statedCreditRequired = true;
            }
          } else {
            const statusValue =
              typeof (finalResponseData as any)?.status === 'string' ? ((finalResponseData as any).status as string).toLowerCase() : (finalResponseData as any)?.status;
            statedCreditRequired = statusValue === 'failed' || statusValue === null;
          }

          if (!isResume && statedCreditRequired) {
            setScoreNeeded(true);
            dispatch(stopLoading());
          } else if (isResume && (statedCreditRequired || finalResponseData?.borrowers?.[0]?.statedCreditRequired)) {
            setScoreNeeded(true);
            dispatch(stopLoading());
          } else {
            resetChangeDetection && resetChangeDetection();

            if (isICE) {
              if (!didValidateRef.current) {
                didValidateRef.current = true;
                try {
                  dispatch(startLoading());
                  const validateAction = {
                    actionProperties: {
                      endpoint: `/credit-score/%%loanId%%/validate`,
                    },
                  };
                  const validatePayload = {
                    creditScore: Number(auxLoanApplication.borrowers?.[0]?.creditScore) || 850,
                  };
                  await makePatchCall(validateAction as any, auxLoanApplication, validatePayload, dispatch, templateConfig);
                } catch (e) {
                  errorRef.current = e;
                  openErrorModalRef.current = true;
                  forceUpdate({});
                  dispatch(stopLoading());
                  return;
                } finally {
                  dispatch(stopLoading());
                }
              }
            }

            closeModal('next');
          }

          return;
        }

        let payload = isICE ? createIcePayload(loanApplication, 'loans') : loanApplication;
        if (isSoftCreditPullSelected && !isICE) {
          payload = { ...payload, preventSoftPullCredit: false };
        }

        // if ('preventSoftPullCredit' in payload) {
        // } else {
        // }
        const newAction = {
          actionProperties: {
            endpoint,
            method,
          },
        };

        let softPullResponse = hasLoanInfo
          ? await makePatchCall(newAction, loanApplication, payload, dispatch, templateConfig)
          : await makePostCall(newAction, loanApplication, payload, dispatch, templateConfig);

        const responseData = (softPullResponse && (softPullResponse as any).payload) ?? (softPullResponse && (softPullResponse as any).data) ?? softPullResponse;

        if (isICE && responseData && responseData.loanId) {
          auxLoanApplication.loanId = responseData.loanId;
          auxLoanApplication.loanNumber = responseData.loanNumber || auxLoanApplication.loanNumber;
          dispatch({ type: 'LoanApplication/updateSpecificField', payload: { depth: ['loanId'], value: auxLoanApplication.loanId } });
          if (auxLoanApplication.loanNumber) {
            dispatch({ type: 'LoanApplication/updateSpecificField', payload: { depth: ['loanNumber'], value: auxLoanApplication.loanNumber } });
          }
        }

        if (isICE && responseData && isSoftCreditPullSelected) {
          dispatch(startLoading());
          auxLoanApplication.loanId = responseData?.loanId;
          auxLoanApplication.loanNumber = responseData?.loanNumber;

          dispatch({
            type: 'LoanApplication/updateSpecificField',
            payload: {
              depth: ['loanId'],
              value: auxLoanApplication.loanId,
            },
          });
          dispatch({
            type: 'LoanApplication/updateSpecificField',
            payload: {
              depth: ['loanNumber'],
              value: auxLoanApplication.loanNumber,
            },
          });

          const endpoint = `/loans/${auxLoanApplication.loanId}/soft-credit-pull`;
          const method = 'POST';

          // let softPullPayload = { ...createIcePayload(loanApplication, 'softpull'), preventSoftPullCredit: false };
          let softPullPayload = { ...createIcePayload(loanApplication, 'softpull') };

          const newAction = {
            actionProperties: {
              endpoint,
              method,
            },
          };
          softPullResponse = await makePostCall(newAction, auxLoanApplication, softPullPayload, dispatch, templateConfig);
        }

        const effectiveData = (softPullResponse && ((softPullResponse as any)?.payload ?? (softPullResponse as any)?.data ?? softPullResponse)) || responseData;
        let statedCreditRequired = false;
        if (Array.isArray(effectiveData)) {
          const borrowersItem = effectiveData.find((item: { depth?: string[]; payload?: { statedCreditRequired?: boolean } }) => item.depth?.includes('borrowers'));
          if (borrowersItem && typeof borrowersItem.payload?.statedCreditRequired === 'boolean') {
            statedCreditRequired = borrowersItem.payload.statedCreditRequired;
          } else {
            statedCreditRequired = true;
          }
        } else {
          const statusValue = typeof (effectiveData as any)?.status === 'string' ? ((effectiveData as any).status as string).toLowerCase() : (effectiveData as any)?.status;
          statedCreditRequired = statusValue === 'failed' || statusValue === null;
        }

        if (!isResume && statedCreditRequired) {
          setScoreNeeded(true);
          dispatch(stopLoading());
        } else if (isResume && (statedCreditRequired || (effectiveData as any)?.borrowers?.[0]?.statedCreditRequired)) {
          setScoreNeeded(true);
          dispatch(stopLoading());
        } else {
          // reset change detection after successful API call
          resetChangeDetection && resetChangeDetection();

          if (isICE) {
            if (!didValidateRef.current) {
              didValidateRef.current = true;
              try {
                dispatch(startLoading());
                const validateAction = {
                  actionProperties: {
                    endpoint: `/credit-score/%%loanId%%/validate`,
                  },
                };
                const validatePayload = {
                  creditScore: Number(auxLoanApplication.borrowers?.[0]?.creditScore) || 850,
                };
                await makePatchCall(validateAction as any, auxLoanApplication, validatePayload, dispatch, templateConfig);
              } catch (e) {
                errorRef.current = e;
                openErrorModalRef.current = true;
                forceUpdate({});
                dispatch(stopLoading());
                return;
              } finally {
                dispatch(stopLoading());
              }
            }
          }

          closeModal('next');
        }
      } else {
        dispatch(startLoading());
        const updatedBorrower = {
          ...auxLoanApplication.borrowers[0],
          creditScoreStated: true,
        };
        auxLoanApplication.borrowers[0] = updatedBorrower;
        !isICE && (await performStatedCreditPatch(auxLoanApplication, dispatch, templateConfig));
        // reset change detection after successful API call
        resetChangeDetection && resetChangeDetection();
      }
    } catch (error) {
      errorRef.current = error;
      openErrorModalRef.current = true;
      forceUpdate({});
      console.error('Error processing click:', error);
    } finally {
      dispatch(stopLoading());
    }
  };

  return (
    <>
      <BackDrop $isOpen={openModal} onClick={closeModal} data-testid="modal-backdrop" />
      {!errorRef.current && (
        <Modal $isOpen={openModal} $isLoading={isLoading}>
          {isLoading && (
            <ModalBody>
              <Spinner />
              <ModalTitle $isLoading={isLoading}>
                {templateConfig?.pages?.LetsGetStarted?.modals?.loadingModal?.textOne || pages?.LetsGetStarted?.modals?.loadingModal?.textOne}
              </ModalTitle>
              <ModalContent $isLoading={isLoading}>
                {templateConfig?.pages?.LetsGetStarted?.modals?.loadingModal?.textTwo || pages?.LetsGetStarted?.modals?.loadingModal?.textTwo}
              </ModalContent>
            </ModalBody>
          )}
          {!isLoading && scoreNeeded && (
            <ModalBody>
              <ModalTitle $isLoading={isLoading}>{templateConfig?.pages?.LetsGetStarted?.modals?.creditScoreNeeded?.title || 'Credit Score Needed'}</ModalTitle>
              <ModalContent $isLoading={isLoading}>
                <SafeHTML
                  tag="p"
                  html={
                    templateConfig?.pages?.LetsGetStarted?.modals?.creditScoreNeeded?.body ||
                    'We were unable to obtain credit with the information provided. <b>Please select the best estimate of your credit score below to continue.'
                  }
                />
              </ModalContent>
              <Slider value={creditScore} min={globalMin} max={globalMax} scoreConfig={sliderConfig} onChange={handleSliderChange} />
              <Divider />
              <TextInput
                dataTestId="credit-score-input"
                type="number"
                value={creditScore.toString()}
                onChange={handleInputChange}
                minAmount={globalMin}
                maxAmount={globalMax}
                isCentered
              />
              <ModalFooter>
                <Button
                  label={templateConfig?.pages?.LetsGetStarted?.modals?.creditScoreNeeded?.button || 'Submit'}
                  onClick={async () => {
                    await processClick();
                    if (!errorRef.current) {
                      if (templateConfig?.isICE) {
                        if (!didValidateRef.current) {
                          didValidateRef.current = true;
                          try {
                            dispatch(startLoading());
                            const validateAction = {
                              actionProperties: {
                                endpoint: `/credit-score/%%loanId%%/validate`,
                              },
                            };
                            const validatePayload = {
                              creditScore: Number(loanApplication?.borrowers?.[0]?.creditScore) || 850,
                            };
                            await makePatchCall(validateAction as any, loanApplication, validatePayload, dispatch, templateConfig);
                          } catch (e) {
                            errorRef.current = e;
                            openErrorModalRef.current = true;
                            forceUpdate({});
                            dispatch(stopLoading());
                            return;
                          } finally {
                            dispatch(stopLoading());
                          }
                        }
                      }
                      closeModal('next');
                    }
                  }}
                  variant="primary"
                  buttonSize="l"
                  isDisabled={isCreditScoreError}
                />
              </ModalFooter>
            </ModalBody>
          )}
          {!isLoading && !scoreNeeded && (
            <ModalBody>
              <ModalTitle $isLoading={isLoading}>{infoSubmitModal?.header}</ModalTitle>
              <ModalContent $isLoading={isLoading}>
                <SafeHTML tag="p" html={infoSubmitModal?.body || ''} />
              </ModalContent>
              <ModalInput>{infoSubmitModal?.label}</ModalInput>
              <DatePicker
                dataTestId="date-of-birth"
                propertyType={'dateBirthday'}
                propertyName={'birthData'}
                placeholder={'MM/DD/YYYY'}
                isHidden={false}
                value={loanApplication?.borrowers[0]?.borrowerDOB || ''}
                setIsValidDateOfBirth={setIsValidDateOfBirth}
                isSentitiveInfo={true}
              />
              <ModalFooter>
                <Button
                  isDisabled={!canContinue || !isValidDateOfBirth}
                  label={infoSubmitModal.button}
                  onClick={async () => {
                    await processClick();
                  }}
                  variant="primary"
                  buttonSize="l"
                />
              </ModalFooter>
            </ModalBody>
          )}
        </Modal>
      )}
      {openErrorModalRef.current && errorRef.current && (
        <DefaultModal
          openModal={openErrorModalRef.current}
          setOpenModal={(open: boolean) => {
            openErrorModalRef.current = open;
            forceUpdate({});
          }}
          isErrorModal={openErrorModalRef.current}
          infoModal={{
            header: (errorRef.current as any)?.errorResponse?.response?.error?.appActionTitle || 'Error',
            body: typeof errorRef.current === 'string' ? errorRef.current : (errorRef.current as any)?.message || 'An error occurred',
            button: (errorRef.current as any)?.errorResponse?.response?.error?.appActionButtonTitle || 'Return to %%lenderName%%',
          }}
          preventBackgroundInteraction={true}
        />
      )}
    </>
  );
};
